import type { PropsWithChildren } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { z } from 'zod';
import {
  useFormSettingsQuery,
  useUpdateFormSettingsMutation,
} from '../../../graphql';
import useEvent from '../../hooks/useEvent';
import { Box, Button, DropdownButton, Menu, MenuItem, Text } from '../';
import { IconWrapper } from '../../Icons';
import themeGet from '@styled-system/theme-get';
import { toast } from 'react-toastify';
import EyeSvg from '../../../images/icon-eye.svg';
import EyeSlashSvg from '../../../images/icon-eye-slash.svg';
import SettingsIconSvg from '../../../images/settings.svg';

/** List of forms that have this functionality enabled */
const KNOWN_FORMS = [
  'voyage_charter_coa',
  'voyage_charter_inquiry',
  'voyage_charter_spot',
] as const;

const formSettingsSchema = z.object({
  forms: z.record(
    z.enum(KNOWN_FORMS),
    z.object({
      hiddenElements: z.array(z.string()),
    }),
  ),
});

type FormSettings = z.infer<typeof formSettingsSchema>;

const defaultFormSettings: FormSettings = {
  forms: {},
};

type FormSettingsContextValue = {
  isEditingVisibility: boolean;
  hiddenElements: Set<string>;
  toggleElementVisibility: (
    visibilityId: string,
    newVisibility: boolean,
  ) => void;
  toggleVisibilityEditing: () => void;
};

const FormSettingsContext = createContext<FormSettingsContextValue | undefined>(
  undefined,
);

export function FormSettingsProvider(
  props: PropsWithChildren<{ form: (typeof KNOWN_FORMS)[number] }>,
) {
  const [isEditingVisibility, setIsEditingVisibility] =
    useState<boolean>(false);

  const { data: queryData, loading: queryLoading } = useFormSettingsQuery();
  const [
    updateFormSettings,
    { loading: mutationLoading, error: mutationError },
  ] = useUpdateFormSettingsMutation();

  const userSettingsId =
    queryData?.currentUser?.userSettingsByUserId.nodes.at(0)?.id ?? null;
  const persistedSettings: FormSettings = useMemo(() => {
    const settings = queryData?.currentUser?.userSettingsByUserId.nodes.at(0);
    if (!settings?.formSettings) return defaultFormSettings;

    try {
      const parsed = formSettingsSchema.parse(
        JSON.parse(settings.formSettings),
      );
      return parsed;
    } catch (parseError) {
      console.warn('Could not parse persisted form settings', parseError);
    }

    return defaultFormSettings;
  }, [queryData?.currentUser?.userSettingsByUserId.nodes]);

  const currentFormSettings = persistedSettings.forms[props.form];

  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>(
    {},
  );

  const hiddenElements = useMemo(() => {
    const hiddenElements = new Set(currentFormSettings?.hiddenElements ?? []);

    for (const [visibilityId, newVisibility] of Object.entries(
      pendingChanges,
    )) {
      if (newVisibility) {
        hiddenElements.delete(visibilityId);
      } else {
        hiddenElements.add(visibilityId);
      }
    }

    return hiddenElements;
  }, [currentFormSettings?.hiddenElements, pendingChanges]);

  const toggleVisibilityEditing = useCallback(() => {
    setIsEditingVisibility((curValue) => !curValue);
  }, []);

  const toggleElementVisibility = useCallback(
    (visibilityId: string, newVisibility: boolean) => {
      setPendingChanges((curPendingChanges) => {
        return {
          ...curPendingChanges,
          [visibilityId]: newVisibility,
        };
      });
    },
    [],
  );

  const saveChanges = useCallback(() => {
    if (userSettingsId === null) return;

    const newSettings: FormSettings = {
      ...persistedSettings,
      forms: {
        [props.form]: {
          ...currentFormSettings,
          hiddenElements: Array.from(hiddenElements),
        },
      },
    };

    try {
      const parsed = formSettingsSchema.parse(newSettings);

      updateFormSettings({
        variables: {
          id: userSettingsId,
          formSettings: JSON.stringify(parsed),
        },
      });

      setPendingChanges({});
      setIsEditingVisibility(false);
    } catch (err) {
      console.error('Error saving form settings', err);
      toast.error('Saving form settings failed');
    }
  }, [
    currentFormSettings,
    hiddenElements,
    persistedSettings,
    props.form,
    updateFormSettings,
    userSettingsId,
  ]);

  const discardChanges = useCallback(() => {
    setPendingChanges({});
    setIsEditingVisibility(false);
  }, []);

  const value = {
    isEditingVisibility,
    hiddenElements,
    toggleElementVisibility,
    toggleVisibilityEditing,
  };

  return (
    <FormSettingsContext.Provider value={value}>
      {queryLoading && !queryData ? null : props.children}
      {isEditingVisibility && (
        <FormSettingsOverlay
          onSave={saveChanges}
          onCancel={discardChanges}
          isLoading={mutationLoading}
          error={mutationError?.message}
        />
      )}
    </FormSettingsContext.Provider>
  );
}

const Overlay = styled.div`
  position: absolute;
  top: ${themeGet('space.4')}px;
  /** right: ${themeGet('space.5')}px; */
  left: 50%;
  transform: translateX(-50%);
  width: 20rem;
  padding: ${themeGet('space.4')}px;
  background-color: ${themeGet('colors.neutral.white_100')};
  border: 1px solid ${themeGet('colors.orange.orange_500')};
`;

function FormSettingsOverlay(props: {
  error?: string;
  isLoading: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <Overlay>
      <Box mb={3}>
        <Text fontWeight="bold">Editing form settings</Text>
      </Box>
      <Box>
        <Button
          variant="primary"
          mr={2}
          onPress={props.onSave}
          disabled={props.isLoading}
        >
          Save
        </Button>
        <Button
          variant="secondary"
          onPress={props.onCancel}
          disabled={props.isLoading}
        >
          Cancel
        </Button>
      </Box>
      {props.error && (
        <Box mt={3}>
          <Text color="status.error" fontWeight="bold">
            Error saving: {props.error}
          </Text>
        </Box>
      )}
    </Overlay>
  );
}

export function useFormVisibility(visibilityId?: string) {
  const context = useContext(FormSettingsContext);

  const isEditingVisibility = context ? context.isEditingVisibility : false;

  /** Is editing of visibility for this element possible */
  const isVisibilityEditEnabled =
    context && visibilityId ? context.isEditingVisibility : false;

  const isVisible =
    context && visibilityId ? !context.hiddenElements.has(visibilityId) : true;

  const onVisibilityToggle = useEvent(() => {
    if (context && visibilityId) {
      context.toggleElementVisibility(visibilityId, !isVisible);
    }
  });

  return {
    isEditingVisibility,
    isVisible,
    isVisibilityEditEnabled,
    onVisibilityToggle,
  };
}

const VisibilityToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  margin-right: 2px;
  border: none;
  border-radius: 2px;
  background-color: transparent;
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

export function FormVisibilityToggle(props: {
  onClick: () => void;
  isVisible: boolean;
}) {
  function onClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    props.onClick();
  }

  return (
    <VisibilityToggleButton onClick={onClick}>
      {props.isVisible ? (
        <img src={EyeSvg} width={16} height={16} />
      ) : (
        <img src={EyeSlashSvg} width={16} height={16} />
      )}
    </VisibilityToggleButton>
  );
}

export const VisibilityOverlay = styled.div<{
  disablePointerEventsWhileEditing?: boolean;
  isEditingVisibility: boolean;
  isVisible: boolean;
}>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  filter: ${(props) =>
    props.isEditingVisibility && !props.isVisible ? 'grayscale(100%)' : 'none'};
  opacity: ${(props) =>
    props.isEditingVisibility && !props.isVisible ? '0.25' : '1'};
  pointer-events: ${(props) =>
    props.isEditingVisibility &&
    (props.disablePointerEventsWhileEditing || !props.isVisible)
      ? 'none'
      : 'initial'};
  user-select: ${(props) => (props.isEditingVisibility ? 'none' : 'initial')};
`;

export function FormVisibilityBox(
  props: PropsWithChildren<{
    visibilityId: string;
  }>,
): JSX.Element | null {
  const {
    isEditingVisibility,
    isVisible,
    isVisibilityEditEnabled,
    onVisibilityToggle,
  } = useFormVisibility(props.visibilityId);

  if (!isVisible && !isVisibilityEditEnabled) return null;
  if (!isEditingVisibility) return <>{props.children}</>;

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
    >
      {isVisibilityEditEnabled && (
        <FormVisibilityToggle
          isVisible={isVisible}
          onClick={onVisibilityToggle}
        />
      )}
      <VisibilityOverlay
        isEditingVisibility={isEditingVisibility}
        isVisible={isVisible}
      >
        {props.children}
      </VisibilityOverlay>
    </Box>
  );
}

export function FormSettingsDropdown() {
  const context = useContext(FormSettingsContext);

  if (!context) {
    console.warn(
      'Should only use FormSettingsDropdown inside FormSettingsContext',
    );
    return null;
  }

  return (
    <DropdownButton
      label={
        <IconWrapper>
          <img src={SettingsIconSvg} width={14} height={18} />
        </IconWrapper>
      }
    >
      <Menu hasShadow>
        <MenuItem
          label="Edit form settings"
          onPress={context.toggleVisibilityEditing}
        />
      </Menu>
    </DropdownButton>
  );
}
